import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { DealBookingType } from '@src/types/OTC';
import type {
  DealProductBuilderRow,
  FixedRateLoanDealProductBuilderRow,
  FloatingRateLoanDealProductBuilderRow,
  ForwardDealProductBuilderRow,
  OptionDealProductBuilderRow,
  OTCFieldsData,
  RepoDealProductBuilderRow,
} from '@src/types/OTC/otcProductBuilderTypes';
import {
  fixingIndexMappingMapToRecord,
  fixingIndexMappingRecordToMap,
  type FixingIndexMappingMap,
  type FixingIndexMappingRecord,
} from '@src/utils/fixtures/OTC/fixingFeedUtils';
import { OptionTypeEnum } from '@talos/kyoko';
import type { Draft } from 'immer';
import { v4 as uuid } from 'uuid';
import {
  validateAndUpdateAllRowsProductBuilderFormState,
  validateAndUpdateProductBuilderFormState,
} from './dealProductBuilderValidation';
import { getSliceStrategyForOTCFields, getSliceStrategyForRowActions } from './productDefinitions/getSliceStrategy';

export interface DealProductBuilderState {
  rowData: [DealProductBuilderRow, ...DealProductBuilderRow[]];
  isFormValid: boolean;
  error: string | null;
  validationErrors: Record<string, string> | null;
  fixingIndexMapping?: FixingIndexMappingRecord;
}

const initialState: DealProductBuilderState = {
  rowData: getInitialRowData({ product: 'forward' }),
  isFormValid: false,
  error: null,
  validationErrors: null,
  fixingIndexMapping: undefined,
};

export function getInitialRowData(initData: { product: DealBookingType }): [DealProductBuilderRow] {
  const baseRow = {
    id: uuid(),
    leg: 1,
    productTemplate: null,
    uniqueSuffix: null,
    otcFields: null,
    isLegValid: false,
  };

  switch (initData.product) {
    case 'forward':
      return [
        {
          ...baseRow,
          product: 'forward',
          baseAsset: null,
          otcFields: null,
        } satisfies ForwardDealProductBuilderRow,
      ];
    case 'fixedRateLoan':
      return [
        {
          ...baseRow,
          product: 'fixedRateLoan',
          baseAsset: null,
          collateralAsset: null,
          otcFields: null,
        } satisfies FixedRateLoanDealProductBuilderRow,
      ];
    case 'floatingRateLoan':
      return [
        {
          ...baseRow,
          product: 'floatingRateLoan',
          baseAsset: null,
          collateralAsset: null,
          otcFields: null,
        } satisfies FloatingRateLoanDealProductBuilderRow,
      ];
    case 'option':
      return [
        {
          ...baseRow,
          product: 'option',
          baseAsset: null,
          optionType: OptionTypeEnum.Call,
          otcFields: null,
        } satisfies OptionDealProductBuilderRow,
      ];
    case 'repo':
      return [
        {
          ...baseRow,
          product: 'repo',
          baseAsset: null,
          collateralAsset: null,
          otcFields: null,
        } satisfies RepoDealProductBuilderRow,
      ];
  }
}

const resetFormState = (state: Draft<DealProductBuilderState>, product: DealBookingType): void => {
  state.rowData = getInitialRowData({ product });
  state.isFormValid = false;
  state.error = null;
  state.validationErrors = null;
  // Preserve fixingIndexMapping when resetting form
};

const dealProductBuilderSlice = createSlice({
  name: 'dealProductBuilder',
  initialState,
  reducers: {
    updateGridRowData: (state, action: PayloadAction<{ rowIndex: number; data: DealProductBuilderRow }>) => {
      const { rowIndex, data: actionRowData } = action.payload;
      const rowData = state.rowData.at(rowIndex);
      if (!rowData) {
        return;
      }

      if (rowData.product !== actionRowData.product) {
        state.error = 'Updates to different product types should not happen';
        return;
      }

      state.error = null;

      const { sliceStrategy, typedExistingData, typedNewData } = getSliceStrategyForRowActions(rowData, actionRowData);
      const fixingIndexMapping = fixingIndexMappingRecordToMap(state.fixingIndexMapping);
      sliceStrategy.updateGridRowData(typedExistingData, typedNewData, fixingIndexMapping);
      validateAndUpdateProductBuilderFormState(state, state.rowData, rowIndex);
    },
    updateOTCFields: (state, action: PayloadAction<{ rowIndex: number; otcFields: OTCFieldsData }>) => {
      const { rowIndex, otcFields } = action.payload;
      const currentRow = state.rowData.at(rowIndex);

      if (!currentRow) {
        return;
      }

      const { sliceStrategy, typedCurrentRow, typedOtcFields } = getSliceStrategyForOTCFields(currentRow, otcFields);
      const fixingIndexMapping = fixingIndexMappingRecordToMap(state.fixingIndexMapping);
      sliceStrategy.updateOTCFields(typedCurrentRow, typedOtcFields, fixingIndexMapping);
      validateAndUpdateProductBuilderFormState(state, state.rowData, rowIndex);
    },
    clearError: state => {
      state.error = null;
    },
    resetForm: (state, action: PayloadAction<{ bookingType: DealBookingType }>) => {
      resetFormState(state, action.payload.bookingType);
    },
    setBookingType: (state, action: PayloadAction<DealBookingType>) => {
      resetFormState(state, action.payload);
    },
    clearForm: (state, action: PayloadAction<DealBookingType>) => {
      resetFormState(state, action.payload);
    },
    setFixingIndexMapping: (
      state,
      action: PayloadAction<FixingIndexMappingMap | FixingIndexMappingRecord | undefined>
    ) => {
      const payload = action.payload;
      // Convert Map to Record if needed, otherwise use Record directly
      state.fixingIndexMapping = payload instanceof Map ? fixingIndexMappingMapToRecord(payload) : payload;
      // Revalidate all rows when mapping changes, as validation depends on fixing availability
      validateAndUpdateAllRowsProductBuilderFormState(state);
    },
  },
});

export const {
  updateGridRowData,
  updateOTCFields,
  clearError,
  resetForm,
  setBookingType,
  clearForm,
  setFixingIndexMapping,
} = dealProductBuilderSlice.actions;

export type DealProductBuilderActionCreators = typeof dealProductBuilderSlice.actions;
export type DealProductBuilderAction = ReturnType<
  DealProductBuilderActionCreators[keyof DealProductBuilderActionCreators]
>;
export const dealProductBuilderSliceReducer = dealProductBuilderSlice.reducer;
